import { graphql } from 'react-relay';
import type { FC, ReactNode } from 'react';
import React, { useState, memo, useCallback } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { useIntl } from 'react-intl';

import Section from '@atlaskit/menu/section';

import AtlassianAccountMenu from '@atlassian/account-menu/nav4/account-menu-nav4';
// eslint-disable-next-line no-restricted-imports
import { isIsolatedCloud } from '@atlaskit/atlassian-context/is-isolated-cloud';

import { SYSTEM_USER } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useWebItemLocation } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePersistentUpgradeMenuSection } from '@confluence/persistent-upgrade/entry-points/usePersistentUpgradeMenuSection.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { clearOfflineStepsDatabases } from '@confluence/editor-offline-storage/entry-points/clear-offline-steps-databases.ts';
import { useTrashedSpace } from '@confluence/space-directory/entry-points/useTrashedSpace.ts';
import { fg } from '@confluence/feature-gating';
import { getLoginLink } from '@confluence/login-if-anonymous/entry-points/getLoginLink.ts';
import { useThemingState } from '@confluence/theming/entry-points/useThemingState.ts';
import { fetchGlobalColorMode } from '@confluence/theming/entry-points/useGlobalColorMode.ts';
import { useColorModeState } from '@atlassian/shared-user-preferences-api/global-theme-management';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import type {
	AccountMenuTrashedPersonalSpacesQuery as AccountMenuTrashedPersonalSpacesQuery$data,
	AccountMenuTrashedPersonalSpacesQueryVariables as AccountMenuTrashedPersonalSpacesQuery$variables,
} from './__types__/AccountMenuTrashedPersonalSpacesQuery.ts';
import type { FilteredItems } from './useWebItemMapper.tsx';
import { useWebItemMapper } from './useWebItemMapper.tsx';
import type { WebItem } from './mapWebItem.tsx';
import { PopupMenuItemComponent } from './PopupMenuItem.tsx';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const AccountMenuTrashedPersonalSpacesQuery = graphql`
	query AccountMenuTrashedPersonalSpacesQuery($creatorAccountIds: [String!]) {
		spaces(type: "PERSONAL", creatorAccountIds: $creatorAccountIds, status: "TRASHED") {
			nodes {
				alias
				key
				id
			}
		}
	}
`;

/* Excluded web items include 'confluence.user.menu:user-favourites' (carried over from ProfileMenu)
 * as well as web items that are already supported by the platform Account Menu.
 * We are passing in an excluded list instead of an included list to avoid missing any web items.
 * Currently expected web items include the following:
 * - com.atlassian.confluence.plugins.confluence-frontend-support:adg3-give-feedback
 * - confluence.user.menu:create-personal-space
 * - confluence.user.menu:personal-space
 * - confluence.user.menu:admin-restore-personal-space
 * - com.atlassian.confluence.plugins.confluence-inline-tasks:my-tasks-menu-item
 * - confluence.user.menu:settings
 * - com.atlassian.plugins.atlassian-connect-plugin:confluence-chats-integration__profile
 * - extension points (begin with com.atlassian.plugins.atlassian-connect-plugin:connect-playground-confluence)
 */

const EXCLUDED_WEB_ITEMS = [
	'confluence.user.menu:user-favourites',
	'confluence.user.menu:user-profile',
	'confluence.user.menu:user-theme',
	'confluence.user.menu:logout',
];

type emails = {
	value: string | null;
};

type photos = {
	value: string | null;
};

type User = {
	id: string | null;
	displayName: string | null;
	emails?: (emails | null)[] | null;
	photos: (photos | null)[] | null;
};

type AccountMenuProps = {
	user: User;
	dialogFn(arg: (onClose: () => void) => ReactNode): void;
};

type AccountMenuComponentProps = AccountMenuProps & {
	webItems: WebItem[];
	webSections: (string | null)[];
	dialogs: DialogsStateContainer;
	flags: FlagsStateContainer;
};

const getManageAccountUrl = (destinationAccountEmail: string, environment: string) => {
	const loginLink = getLoginLink(getAtlassianPersonalSettingsLink(environment));

	return `${loginLink}&login_hint=${encodeURIComponent(destinationAccountEmail)}&prompt=none`;
};

const getSwitchAccountUrl = () => {
	const loginLink = getLoginLink();

	return `${loginLink}&prompt=select_account`;
};

const manageProfileUrlSuffix = 'manage-profile/profile-and-visibility';
const getAtlassianPersonalSettingsLink = (environment: string) => {
	if (isIsolatedCloud()) {
		// dynamically generate identity URL based on IC shard (defined in /confluence/next/tools/config/env/)
		return `https://${process.env.IDENTITY_PLATFORM}/${manageProfileUrlSuffix}`;
	} else {
		return environment === 'PRODUCTION'
			? `https://id.atlassian.com/${manageProfileUrlSuffix}`
			: `https://id.stg.internal.atlassian.com/${manageProfileUrlSuffix}`;
	}
};

const AccountMenuComponent: FC<AccountMenuComponentProps> = memo(
	({ user, webItems, webSections, dialogFn, flags }) => {
		const { formatMessage } = useIntl();
		const { environment } = useSessionData();
		const getThemePreference = useCallback(() => fetchGlobalColorMode(user?.id), [user?.id]);
		const [globalColorMode, loadGlobalColorModeOnce] = useColorModeState(
			getThemePreference,
			user?.id,
		);

		const userId = user?.id || '';
		const profileIconUrl = user?.photos?.[0]?.value || '';
		const accountEmail = user?.emails?.[0]?.value || '';
		const accountDisplayName = user?.displayName || '';
		const manageAccountUrl = getManageAccountUrl(accountEmail, environment);
		const switchAccountUrl = getSwitchAccountUrl();

		const [themeState, themingActions] = useThemingState();
		const persistentUpgradeMenuSection = usePersistentUpgradeMenuSection() as Array<FilteredItems>;
		const [itemSelected, setItemSelected] = useState<string | undefined>(undefined);

		// if webItems has 'admin-restore-personal-space', then we need to call the query to get the trashed personal space data
		const hasTrashedPersonalSpace = webItems.some(
			(obj) => obj.completeKey === 'confluence.user.menu:admin-restore-personal-space',
		);
		const { data } = useQueryInterop<
			AccountMenuTrashedPersonalSpacesQuery$data,
			AccountMenuTrashedPersonalSpacesQuery$variables
		>(AccountMenuTrashedPersonalSpacesQuery, {
			skip: !hasTrashedPersonalSpace,
			variables: {
				creatorAccountIds: [userId || ''],
			},
		});
		const node = data?.spaces?.nodes?.[0];
		const spaceKey = node?.key || '';
		const spaceId = node?.id || '';
		const spaceAlias = node?.alias || '';

		const { handleRestoreClick } = useTrashedSpace({
			spaceKey,
			spaceAlias,
			spaceId,
			flags,
			source: 'profileMenu',
		});

		const { createEvent } = useConfluenceAnalyticsEvents();

		const callbackActions = {
			'user-theme': () => {
				itemSelected === 'user-theme' ? setItemSelected(undefined) : setItemSelected('user-theme');
			},
			'admin-restore-personal-space': () => handleRestoreClick(),
			'personal-space': () => {
				createEvent({
					type: 'sendUIEvent',
					data: {
						source: 'profileMenu',
						action: 'clicked',
						actionSubject: 'menuItem',
						actionSubjectId: 'personalSpace',
					},
				}).fire();
			},
		};

		const menuItems: Array<FilteredItems> = useWebItemMapper(webItems, webSections, {
			userId,
			callbackActions,
		});

		const accountMenuItems = [...persistentUpgradeMenuSection, ...menuItems];

		const getSectionTitle = (group: FilteredItems) => {
			if (group.heading) {
				if (typeof group.heading === 'string') {
					return group.heading;
				} else {
					return formatMessage(group.heading);
				}
			} else {
				return undefined;
			}
		};

		const onLogoutClickCallback = (_event, _analyticsEvent) => {
			clearOfflineStepsDatabases();
		};

		return (
			<AtlassianAccountMenu
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				globalColorMode={fg('shared-user-preferences') ? globalColorMode : undefined}
				onThemeSwitcherOpenChange={loadGlobalColorModeOnce}
				testId="confluence-account-menu"
				name={accountDisplayName}
				picture={profileIconUrl}
				trustPicture
				email={accountEmail}
				viewProfileLink="/wiki/users/viewmyprofile.action"
				manageProfileLink={manageAccountUrl}
				logOutLink="/wiki/logout.action"
				currentTheme={themeState?.theme?.colorMode}
				onThemeChange={themingActions.setColorModePreference}
				switchAccountLink={switchAccountUrl}
				onLogoutClick={onLogoutClickCallback}
				extraMenuItems={accountMenuItems.map((group) => (
					<Section key={getSectionTitle(group)} hasSeparator title={getSectionTitle(group)}>
						{group.items.map((item) => {
							const itemGetDialog = item.hasOwnProperty('getDialog') ? item.getDialog : null;
							const dialogHandler = itemGetDialog
								? () => {
										dialogFn(itemGetDialog);
									}
								: null;
							const onClick = item.action || dialogHandler;
							const closeAction = item.closeAction;
							const onClickHandler = () => {
								if (closeAction) {
									closeAction();
								}
								if (onClick) {
									onClick();
								}
							};

							return (
								<PopupMenuItemComponent
									key={item.key}
									item={item}
									onClickHandler={onClickHandler}
									closeMenu={closeAction || (() => {})}
									isSelected={itemSelected === item.key}
								/>
							);
						})}
					</Section>
				))}
			/>
		);
	},
);

export const AccountMenu: FC<AccountMenuProps> = memo(({ user, dialogFn }) => {
	const { webItems, webSections } = useWebItemLocation({
		location: SYSTEM_USER,
		notAllowedWebItems: EXCLUDED_WEB_ITEMS,
	});

	return (
		<Subscribe to={[DialogsStateContainer, FlagsStateContainer]}>
			{(dialogs: DialogsStateContainer, flags: FlagsStateContainer) => {
				const accountMenuProps = {
					user,
					dialogFn,
					webItems,
					webSections,
					dialogs,
					flags,
				};

				return (
					<>
						{/* @ts-expect-error - Type 'WebItem[]' is not assignable to type 'WebItem[]' */}
						<AccountMenuComponent {...accountMenuProps} />
					</>
				);
			}}
		</Subscribe>
	);
});
